/**
 * Single source of truth for column widths. JS owns every width-related CSS
 * variable; SCSS only reads them.
 *
 *   --default-column-width        min(vw, DEFAULT_COLUMN_WIDTH) — the standard
 *                                  sidebar size; used as the fallback when
 *                                  no user preference is set.
 *   --left-column-visual-width    the rendered width of #column-left,
 *                                  computed from: viewport, user preference,
 *                                  open-tabs state.
 *   --left-column-width           the width #column-left occupies in layout
 *                                  flow (what neighbors — chat translateX —
 *                                  should respect). Differs from
 *                                  --left-column-visual-width while the
 *                                  sidebar is collapsed-with-tabs-open: the
 *                                  sidebar visually grows to default but
 *                                  layout still reserves only 80 (overlays
 *                                  the chat).
 *   --right-column-width          right column rendered width (user
 *                                  preference, clamped to MIN/MAX); full vw
 *                                  on handhelds.
 *   --middle-column-width         column-center's layout width (vw - 2 × outer
 *                                  padding).
 *   --middle-column-width-value   numeric copy used by SCSS calc().
 *   --chat-width                  the rendered chat-content max width —
 *                                  CHAT_WIDTH_MAX on docked desktop, the
 *                                  middle-column width when it's tighter
 *                                  (narrow desktop / handheld). Consumed by
 *                                  .chat / .bubbles-inner / topbar plates as
 *                                  a max-width.
 *   --right-sidebar-fits          viewport threshold (px) at which the right
 *                                  column can dock at default size without
 *                                  overlapping a CHAT_WIDTH_MAX-wide chat.
 *                                  Exposed for any SCSS consumer that needs
 *                                  to react to the same threshold the JS
 *                                  layout decision uses; @media queries
 *                                  can't read it, but JS toggles
 *                                  `body.right-column-floats` from the same
 *                                  math, so style your rules against that
 *                                  class instead of a media query.
 *   --page-chats-padding          outer chrome around each column. Owned by
 *                                  JS in two scopes:
 *                                    • :root            16 desktop / 0  handheld
 *                                                       (used by #column-left
 *                                                        and #column-right
 *                                                        margins).
 *                                    • #column-center   16 desktop / 8  handheld
 *                                                       (used by the chat
 *                                                        `inset`, .bubbles
 *                                                        inset-block, fade
 *                                                        math, pinned-call /
 *                                                        pinned-audio
 *                                                        off-screen
 *                                                        translateY — every
 *                                                        bubble-padding
 *                                                        descendant inherits
 *                                                        the same number).
 *
 * Toggles `body.right-column-floats` when the right column overlays the chat
 * (viewport too narrow to fit left + chat + right + paddings side-by-side).
 */

import mediaSizes from '@helpers/mediaSizes';
import rootScope from '@lib/rootScope';
import clamp from '@helpers/number/clamp';
import throttle from '@helpers/schedulers/throttle';
import {getAppWindow} from '@helpers/appWindow';

// CRM-HOOK: the CRM's ticket view lays a board out in the left column - its width, and whether the right column docks
type CrmColumnLayout = (vw: number) => {left: number, rightDocked: boolean} | undefined;
let crmColumnLayout: CrmColumnLayout;
export function setCrmColumnLayout(layout: CrmColumnLayout) {
  crmColumnLayout = layout;
  updateColumnWidths();
}
const crmLayout = (vw: number) => mediaSizes.isMobile ? undefined : crmColumnLayout?.(vw);

// Default and resize range for the left & right columns.
export const DEFAULT_COLUMN_WIDTH = 360;
export const MIN_SIDEBAR_WIDTH = 320;
export const MAX_SIDEBAR_WIDTH = 480;
export const SIDEBAR_COLLAPSE_FACTOR = 0.65;
// Width of #column-left when collapsed. Matches --sidebar-collapsed-width in
// scss/partials/_leftSidebar.scss.
export const SIDEBAR_COLLAPSED_WIDTH = 80;
// Folders sidebar geometry. Owned by JS — written to `--folders-sidebar-width`
// and `--folders-sidebar-offset` so SCSS only consumes the vars.
const FOLDERS_SIDEBAR_WIDTH = 72;
const FOLDERS_SIDEBAR_GAP = 8;
const FOLDERS_SIDEBAR_OFFSET = FOLDERS_SIDEBAR_WIDTH + FOLDERS_SIDEBAR_GAP;

// Outer page padding values, written into `--page-chats-padding` by JS in
// two scopes (see below). The sidebar value collapses to 0 on handheld so
// #column-left / #column-right fill the screen; the chat keeps 8 so the
// floating topbar / chat-input plates have breathing room.
const PAGE_CHATS_PADDING_ROOT_DESKTOP = 16;
const PAGE_CHATS_PADDING_ROOT_HANDHELD = 0;
const PAGE_CHATS_PADDING_CHAT_DESKTOP = 16;
const PAGE_CHATS_PADDING_CHAT_HANDHELD = 8;
// Used in right-column-fits math (desktop threshold).
const PAGE_CHATS_PADDING = PAGE_CHATS_PADDING_ROOT_DESKTOP;
// Visual cap on chat-content max width — bubbles / pinned plates / topbar
// inner row don't grow past this even on huge viewports. Exposed via the
// `--chat-width` CSS variable (clamped to the actually-available middle
// column width on narrower viewports).
const CHAT_WIDTH_MAX = 696;
// Extra spacing used inside the right-sidebar-fit equation — historical
// `+ 64px` from $right-sidebar-fits, kept as-is so the threshold doesn't
// shift.
const RIGHT_SIDEBAR_FITS_EXTRA = 64;

const STORAGE_KEY_LEFT = 'sidebar-left-width';
const STORAGE_KEY_RIGHT = 'sidebar-right-width';

// User-preferred widths for the docked sidebars. Persisted in localStorage.
// `undefined` means "no preference, use DEFAULT_COLUMN_WIDTH". For the left
// column 0 also means "collapsed".
let userPreferredLeftWidth: number | undefined;
let userPreferredLeftCollapsed = false;
let userPreferredRightWidth: number | undefined;

// Transient state: when sidebar is collapsed but a tab is open (Settings
// etc.), it pops out to the full width while the tab remains visible. The
// transition is owned by CSS (transition on width when .is-collapsed),
// so JS only flips the steady-state flag.
let openTabsLeftSidebar = false;

// Whether the folders sidebar is actually visible (preference enabled AND
// viewport wide enough). Mirrors `body.has-folders-sidebar`; pushed in by
// stores/foldersSidebar.ts when the class is toggled.
let foldersSidebarShown = false;

(function loadUserPreferences() {
  const rawLeft = localStorage.getItem(STORAGE_KEY_LEFT);
  if(rawLeft != null) {
    const n = parseInt(rawLeft);
    if(!isNaN(n)) {
      if(n === 0) userPreferredLeftCollapsed = true;
      else userPreferredLeftWidth = clamp(n, MIN_SIDEBAR_WIDTH, MAX_SIDEBAR_WIDTH);
    }
  }
  const rawRight = localStorage.getItem(STORAGE_KEY_RIGHT);
  if(rawRight != null) {
    const n = parseInt(rawRight);
    if(!isNaN(n)) userPreferredRightWidth = clamp(n, MIN_SIDEBAR_WIDTH, MAX_SIDEBAR_WIDTH);
  }
})();

const persistLeftPreference = throttle(() => {
  localStorage.setItem(
    STORAGE_KEY_LEFT,
    (userPreferredLeftCollapsed ? 0 : userPreferredLeftWidth) + ''
  );
}, 200);

const persistRightPreference = throttle(() => {
  localStorage.setItem(STORAGE_KEY_RIGHT, userPreferredRightWidth + '');
}, 200);

/**
 * Called by the left resize handle while the user drags. `width <= 0`
 * records a collapsed preference; any other value is clamped to MIN/MAX
 * before storing.
 */
export function setUserPreferredLeft(width: number): void {
  if(width <= 0) {
    userPreferredLeftCollapsed = true;
    userPreferredLeftWidth = undefined;
  } else {
    userPreferredLeftCollapsed = false;
    userPreferredLeftWidth = clamp(width, MIN_SIDEBAR_WIDTH, MAX_SIDEBAR_WIDTH);
  }
  persistLeftPreference();
  updateColumnWidths();
}

/**
 * Called by the right resize handle while the user drags. The width is
 * clamped to MIN/MAX — the right column has no collapsed state.
 */
export function setUserPreferredRight(width: number): void {
  userPreferredRightWidth = clamp(width, MIN_SIDEBAR_WIDTH, MAX_SIDEBAR_WIDTH);
  persistRightPreference();
  updateColumnWidths();
}

export function isUserCollapsedLeft(): boolean {
  return userPreferredLeftCollapsed;
}

/**
 * Toggle the "open tabs" override. When the sidebar is collapsed and a tab
 * (settings, archive, etc.) opens, the rendered width should be
 * DEFAULT_COLUMN_WIDTH instead of SIDEBAR_COLLAPSED_WIDTH. The actual width
 * change is animated by CSS (transition on #column-left.is-collapsed width).
 */
export function setOpenTabsLeftSidebar(value: boolean): void {
  if(openTabsLeftSidebar === value) return;
  openTabsLeftSidebar = value;
  updateColumnWidths();
}

/**
 * Mirror the folders sidebar visibility into the width math. Adds
 * FOLDERS_SIDEBAR_OFFSET to the docked-layout threshold so the right column
 * starts to float a bit sooner when the folders panel reserves space.
 */
export function setFoldersSidebarShown(value: boolean): void {
  if(foldersSidebarShown === value) return;
  foldersSidebarShown = value;
  updateColumnWidths();
}

function computeVisualLeftWidth(): number {
  const vw = getAppWindow().innerWidth;
  const isMobile = mediaSizes.isMobile;
  const isFloatingLeft = mediaSizes.isLessThanFloatingLeftSidebar && !isMobile;
  const defaultColumnWidth = Math.min(vw, DEFAULT_COLUMN_WIDTH);

  // mobile — SCSS handhelds rule fills the parent grid cell regardless
  // (width: auto; max-width: none). The variable is still consistent.
  if(isMobile) return vw;
  const crm = crmLayout(vw); if(crm) return crm.left; // CRM-HOOK
  // 601-925px — drawer over the chat, fixed at default.
  if(isFloatingLeft) return defaultColumnWidth;
  // Collapsed AND no tabs open → narrow strip. Tabs-open swaps this to the
  // full width; the CSS transition on .is-collapsed animates the change.
  if(userPreferredLeftCollapsed && !openTabsLeftSidebar) return SIDEBAR_COLLAPSED_WIDTH;
  // Steady state: user preference, or default if none.
  return userPreferredLeftWidth ?? defaultColumnWidth;
}

// Width the left column reserves in the layout flow. Equals the visual width
// in most states, but when the sidebar is collapsed and pops out for open
// tabs, it stays at the collapsed width — the expanded sidebar overlays the
// chat instead of pushing it.
function computeLayoutLeftWidth(): number {
  const vw = getAppWindow().innerWidth;
  const isMobile = mediaSizes.isMobile;
  const isFloatingLeft = mediaSizes.isLessThanFloatingLeftSidebar && !isMobile;
  const defaultColumnWidth = Math.min(vw, DEFAULT_COLUMN_WIDTH);

  if(isMobile) return vw;
  const crm = crmLayout(vw); if(crm) return crm.left; // CRM-HOOK
  if(isFloatingLeft) return defaultColumnWidth;
  if(userPreferredLeftCollapsed) return SIDEBAR_COLLAPSED_WIDTH;
  return userPreferredLeftWidth ?? defaultColumnWidth;
}

function computeRightWidth(): number {
  const vw = getAppWindow().innerWidth;
  if(mediaSizes.isMobile) return vw;
  return userPreferredRightWidth ?? Math.min(vw, DEFAULT_COLUMN_WIDTH);
}

const last = {
  visual: -1,
  layout: -1,
  right: -1,
  middle: -1,
  defaultColumn: -1,
  foldersWidth: -1,
  foldersOffset: -1,
  chatWidth: -1,
  rightSidebarFits: -1,
  pageChatsPaddingRoot: -1,
  pageChatsPaddingChat: -1,
  floats: undefined as boolean | undefined
};
let installed = false;

export default function updateColumnWidths(): void {
  const root = document.documentElement;
  const vw = getAppWindow().innerWidth;
  const isMobile = mediaSizes.isMobile;

  // `html` carries the iOS safe-area inset as horizontal padding
  // (`min(16px, env(safe-area-inset-*))` per side — see base.scss). `vw`
  // counts the full viewport including that padding, but every column lays
  // out inside html's content box, which is narrower by up to 32px in
  // landscape (both insets). Use this content-box width (not `vw`) for the
  // horizontal space math below so the chat is sized for the box it actually
  // renders in — otherwise the symmetric gap JS centres the chat into is
  // computed against a wider viewport than exists and collapses against the
  // left bar on landscape iPhones (the inset is 0 on desktop, so this is a
  // no-op there). Read with the other reads at the top to avoid interleaving
  // a style read between the setProperty writes below (layout thrash).
  const rootStyle = getComputedStyle(root);
  const safeAreaPaddingX = (parseFloat(rootStyle.paddingLeft) || 0) + (parseFloat(rootStyle.paddingRight) || 0);
  const availableWidth = vw - safeAreaPaddingX;

  const defaultColumnWidth = Math.min(vw, DEFAULT_COLUMN_WIDTH);
  const visualLeftWidth = computeVisualLeftWidth();
  const layoutLeftWidth = computeLayoutLeftWidth();
  const rightWidth = computeRightWidth();
  // Whether the right column can dock without overlapping the chat. The
  // threshold is the sum of every horizontal piece currently in the docked
  // layout, so resizing either column (or collapsing the left, or toggling
  // the folders panel) immediately re-evaluates the floats decision.
  const crm = crmLayout(vw); // CRM-HOOK
  const foldersOffset = foldersSidebarShown && !crm ? FOLDERS_SIDEBAR_OFFSET : 0;
  const rightColumnFits = foldersOffset + layoutLeftWidth + rightWidth + CHAT_WIDTH_MAX + PAGE_CHATS_PADDING * 4;
  // Handheld is a slider — the right column overlays the chat, so it
  // always floats. Without this body.right-column-floats stays off and the
  // `.bubbles-inner { width: calc(100% - var(--right-column-width) ...) }`
  // rule in _chat.scss subtracts the right column from the chat width,
  // collapsing the messages column to 0 / a tiny centered strip.
  const floats = isMobile || (crm ? !crm.rightDocked : availableWidth < rightColumnFits); // CRM-HOOK
  const middleWidth = isMobile ? vw : availableWidth - PAGE_CHATS_PADDING * 2;
  // Chat content max width — fills the viewport on handheld (single-column
  // slider) and caps at CHAT_WIDTH_MAX otherwise. It also never exceeds the
  // space actually free beside the left column: in the docked-left range
  // (>925px) the left column + folders panel sit in-flow and the chat is
  // centred in the gap to their right (see #column-center translateX in
  // _chat.scss). Subtract them plus 3 paddings — the page's left inset and the
  // two 16px gutters that flank the chat once it's clamped — so the chat keeps
  // a symmetric gap on both sides and a wide/resized left column reclaims width
  // from it. Uses availableWidth (not vw) so the iOS safe-area inset doesn't
  // eat the left gap on landscape iPhones. In the floating-drawer range
  // (601-925) and on mobile the left column overlays the chat, which keeps
  // full width.
  const isFloatingLeft = mediaSizes.isLessThanFloatingLeftSidebar && !isMobile;
  const chatAvailableWidth = (isMobile || isFloatingLeft) ?
    middleWidth :
    availableWidth - foldersOffset - layoutLeftWidth - PAGE_CHATS_PADDING * 3;
  const chatWidth = isMobile ? vw : Math.min(chatAvailableWidth, CHAT_WIDTH_MAX);
  // Viewport threshold at which the right column can dock at default size
  // without overlapping a CHAT_WIDTH_MAX chat. Exposed for any caller that
  // wants to read the same number JS uses for `body.right-column-floats`.
  const rightSidebarFits = DEFAULT_COLUMN_WIDTH * 2 + CHAT_WIDTH_MAX + RIGHT_SIDEBAR_FITS_EXTRA;
  // Outer chrome — split into two scopes so left/right sidebars can drop
  // to 0 on handheld (full-screen slider) while the chat keeps an 8px
  // breathing room for the floating topbar / chat-input plates.
  const pageChatsPaddingRoot = isMobile ? PAGE_CHATS_PADDING_ROOT_HANDHELD : PAGE_CHATS_PADDING_ROOT_DESKTOP;
  const pageChatsPaddingChat = isMobile ? PAGE_CHATS_PADDING_CHAT_HANDHELD : PAGE_CHATS_PADDING_CHAT_DESKTOP;

  if(last.defaultColumn !== defaultColumnWidth) {
    root.style.setProperty('--default-column-width', defaultColumnWidth + 'px');
    last.defaultColumn = defaultColumnWidth;
  }
  if(last.visual !== visualLeftWidth) {
    root.style.setProperty('--left-column-visual-width', visualLeftWidth + 'px');
    last.visual = visualLeftWidth;
  }
  if(last.layout !== layoutLeftWidth) {
    root.style.setProperty('--left-column-width', layoutLeftWidth + 'px');
    last.layout = layoutLeftWidth;
  }
  if(last.right !== rightWidth) {
    root.style.setProperty('--right-column-width', rightWidth + 'px');
    last.right = rightWidth;
  }
  if(last.middle !== middleWidth) {
    root.style.setProperty('--middle-column-width', middleWidth + 'px');
    root.style.setProperty('--middle-column-width-value', '' + middleWidth);
    last.middle = middleWidth;
  }
  if(last.foldersWidth !== FOLDERS_SIDEBAR_WIDTH) {
    root.style.setProperty('--folders-sidebar-width', FOLDERS_SIDEBAR_WIDTH + 'px');
    last.foldersWidth = FOLDERS_SIDEBAR_WIDTH;
  }
  if(last.foldersOffset !== foldersOffset) {
    root.style.setProperty('--folders-sidebar-offset', foldersOffset + 'px');
    last.foldersOffset = foldersOffset;
  }
  if(last.chatWidth !== chatWidth) {
    root.style.setProperty('--chat-width', chatWidth + 'px');
    last.chatWidth = chatWidth;
  }
  if(last.rightSidebarFits !== rightSidebarFits) {
    root.style.setProperty('--right-sidebar-fits', rightSidebarFits + 'px');
    last.rightSidebarFits = rightSidebarFits;
  }
  if(last.pageChatsPaddingRoot !== pageChatsPaddingRoot) {
    root.style.setProperty('--page-chats-padding', pageChatsPaddingRoot + 'px');
    last.pageChatsPaddingRoot = pageChatsPaddingRoot;
  }
  if(last.pageChatsPaddingChat !== pageChatsPaddingChat) {
    const center = document.getElementById('column-center');
    // Skip the cache update if #column-center isn't in the DOM yet (initial
    // module-import run before HTML parse reaches the chat container) so
    // the next call retries.
    if(center) {
      center.style.setProperty('--page-chats-padding', pageChatsPaddingChat + 'px');
      last.pageChatsPaddingChat = pageChatsPaddingChat;
    }
  }
  if(last.floats !== floats) {
    document.body.classList.toggle('right-column-floats', floats);
    last.floats = floats;
  }
}

export function installColumnWidthsUpdater(): void {
  if(installed) return;
  installed = true;
  mediaSizes.addEventListener('resize', updateColumnWidths);
  rootScope.addEventListener('resizing_left_sidebar', updateColumnWidths);
  updateColumnWidths();
}

// Initial paint — set CSS vars as early as the module is imported so the
// sidebars render at the correct width before installColumnWidthsUpdater()
// runs. Subsequent resizes are driven by the mediaSizes listener installed
// in installColumnWidthsUpdater().
updateColumnWidths();
